import { yupResolver } from '@hookform/resolvers/yup';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { useAuth } from '@/api';
import type { Bank, Vpa } from '@/api/digital-gold/bank-details/getBanks';
import { useBankInfo } from '@/api/digital-gold/bank-details/getBanks';
import type { ConfirmPayloadType } from '@/api/digital-gold/confirm';
import { useConfirmOrder } from '@/api/digital-gold/confirm';
import clockIcon from '@/assets/images/icons/clock-icon.svg';
import closeIcon from '@/assets/images/icons/close-icon.svg';
import npci from '@/assets/images/icons/npci.svg';
import shield from '@/assets/images/icons/shield.svg';
import { useSellConfirmStore } from '@/store/digital-gold/sell-confirm-store';
import { useSellVerifyStore } from '@/store/digital-gold/sell-gold-store';
import { upiRegex } from '@/utils/constants/RegexPatterns';

// import type { BankItem, UpiItem } from "@/types";
import Loader from '../Loader';
import { BankItem } from './BankItem';
import { AddBankModal } from './Modals';
import { Timer } from './Timer';
// import { UpiComponent } from "./UpiComponent";
// import { UpiItem } from '@/types';

type CheckoutForm = {
  creditTo: string;
  creditType: string;
  upiId: string;
};

export const SellGoldCheckout = ({
  onCheckout,
}: {
  onCheckout: () => void;
}) => {
  const { userDetails } = useAuth();

  const userId = {
    id: userDetails?.id || '',
    config: {},
  };
  const updateResponse = useSellConfirmStore(
    (state) => state.updateSellConfirmResponse
  );
  const [livePrice, setLivePrice] = useState(null);

  const { data: fetchedBankData } = useBankInfo(userId);

  const openModalref = useRef<HTMLButtonElement>(null);
  const closeModalRef = useRef<HTMLButtonElement>(null);
  const checkOutSchema: any = yup.object({
    creditTo: yup.string().when('upiId', {
      is: (upiId: string) => upiId === '',
      then: (x) => x.required('Please Select One Option'),
    }),
    upiId: yup.lazy(() =>
      yup.string().when('creditTo', {
        is: (creditTo: string) => creditTo === '',
        then: (x) =>
          x
            .required('Enter Valid UPI ID')
            .test('upiId', 'Invalid UPI ID', (value) => {
              return yup
                .string()
                .matches(upiRegex, 'Invalid UPI ID')
                .isValidSync(value);
            }),
      })
    ),
  });

  const banks = (fetchedBankData && fetchedBankData.data.data.bank) || [];
  // const upis = (fetchedBankData && fetchedBankData.data.data.vpa) || [];

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CheckoutForm>({
    defaultValues: {
      creditTo: '',
      creditType: 'bank',
      upiId: '',
    },
    resolver: yupResolver(checkOutSchema),
  });

  const { handleSubmit: confirmSubmit } = useForm({});

  const onSubmit = () => {
    openModalref?.current?.click();
  };

  const bankModalRef = useRef<HTMLAnchorElement>(null);
  const closeBankModal = () => {
    bankModalRef.current?.click();
  };

  const confirmSellOrder = useConfirmOrder();

  const storeVerifyData = useSellVerifyStore();
  const { weight, amountToPay } = storeVerifyData.verifyData;

  const submitConfirm = async () => {
    let dataEl: Bank[] | Vpa[] = [];
    if (getValues('creditType') === 'bank') {
      dataEl =
        (fetchedBankData &&
          fetchedBankData.data.data.bank.filter(
            (item: Bank) => item?.accountNumber === watch('creditTo')
          )) ||
        [];
    }
    // else {
    //   dataEl = fetchedData?.data.data.data.vpa.filter(
    //     (item: Vpa) => item.vpa === watch('creditTo')
    //   ) || [];
    // }
    const payloadData =
      dataEl && dataEl.length > 0 && 'accountNumber' in dataEl[0]
        ? {
            sellLotId: storeVerifyData.verifyData.sellLotId,
            accountNumber: dataEl[0]?.accountNumber,
            ifscCode: dataEl[0].ifscCode,
            accountHolderName: dataEl[0].accountHolderName,
            customerId: userId.id,
            phoneNum: userDetails?.mobilePhone,
          }
        : {
            sellLotId: storeVerifyData.verifyData.sellLotId,
            vpa: getValues('upiId'),
          };
    const payload: ConfirmPayloadType = {
      id: userId.id,
      data: payloadData,
    };
    const confirmResponse = await confirmSellOrder.mutateAsync(payload);

    if (confirmResponse.status === 200) {
      updateResponse(confirmResponse?.data.data);
      onCheckout();
      closeModalRef.current?.click();
    }
  };

  return (
    <>
      <Loader show={confirmSellOrder.isLoading} />
      <button
        data-toggle="modal"
        data-target="#sellConfirmModal"
        ref={openModalref}
        className="d-none"
        // style={{ display: 'none' }}
      ></button>
      <main className="loanDetails pledge repayment checkout visitSchedule__body singleFlow--lightBlue">
        <div className="container">
          {/* @@include('../utils/breadcrumb.html',{"previous":"Digital Gold", "current":"Sell Gold"}) */}
          <div className="visitSchedule">
            <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
              <div className="stepper--content active">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="loanDetails__card loanCard">
                      <div className="loanRequirements">
                        <p className="loanCard__title text--bold text--black">
                          Summary
                        </p>
                        <div className="livePrice withTime">
                          <div className="left">
                            <p className="liveChips text--semi text--white sell_p">
                              <span className="pulsingDot"></span> Live
                            </p>
                            <p className="text--regular text--black title sell_p">
                              Selling Price:{' '}
                              <span className="text--bold">
                                ₹{livePrice}/gm
                              </span>
                            </p>
                          </div>
                          <div className="right">
                            <Image
                              style={{ color: undefined }}
                              className="img-fluid"
                              src={clockIcon}
                              alt="clock icon"
                            />
                            <p className="text--black text--regular time sell_p">
                              Valid for:{' '}
                              <span className="text--bold">
                                <Timer
                                  setLivePrice={setLivePrice}
                                  flowName="SELL"
                                  refreshRequiredPostTime={true}
                                />
                              </span>
                            </p>
                          </div>
                        </div>
                        <p className="text--semi text--black loanCard__title-small">
                          Payment breakdown
                        </p>
                        <div className="payable">
                          <p className="text--gray text--regular desc sell_p">
                            <span className="big">
                              Gold quantity to be sold
                            </span>
                          </p>
                          <p className="text--gray text--semi price sell_p">
                            {weight.toString()} gms
                          </p>
                        </div>
                        <div className="payable final">
                          <p className="text--black text--regular desc sell_p">
                            <span className="big">Total Amount Payable</span>
                          </p>
                          <p className="text--black text--bold price sell_p">
                            ₹ {amountToPay.toFixed(2)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="loanDetails__card loanCard">
                      <div className="loanRequirements">
                        <p className="loanCard__title text--bold text--black sell_p">
                          Credit to
                        </p>
                        <div className="insideCard accordion">
                          <div
                            className="header"
                            id="headingThree"
                            data-toggle="collapse"
                            data-target="#collapseThree"
                            aria-expanded="true"
                            aria-controls="collapseThree"
                          >
                            <p className="text--semi text--black insideCard__title sell_p">
                              Bank account
                            </p>
                          </div>
                          <div
                            id="collapseThree"
                            className="show collapse"
                            aria-labelledby="headingThree"
                            data-parent="#accordion"
                          >
                            <div className="accordion__details">
                              {banks.map((eachAccount: Bank, index: number) => (
                                <BankItem
                                  key={index}
                                  eachAccount={eachAccount}
                                  register={register}
                                  setValue={setValue}
                                />
                              ))}
                              {/* <div className="input-container">
                                {errors?.creditTo &&
                                  getValues('upiId') === '' && (
                                    <p className="text--semi errorMessage withInfo showError ">
                                      {errors?.creditTo?.message}
                                    </p>
                                  )}
                              </div> */}
                              <a
                                className="addUpi text--semi"
                                type="button"
                                data-toggle="modal"
                                data-target="#bankPop"
                              >
                                <span className="plus-icon"></span>Add New Bank
                                Account
                              </a>
                            </div>
                          </div>
                        </div>

                        {/* UPI PAYMENT METHOD  */}
                        <div className="insideCard accordion">
                          <div
                            className="header"
                            id="headingOne"
                            data-toggle="collapse"
                            data-target="#collapseOne"
                            aria-expanded="false"
                            aria-controls="collapseOne"
                          >
                            <p className="text--semi text--black insideCard__title sell_p">
                              UPI
                            </p>
                          </div>
                          <div
                            id="collapseOne"
                            className="collapse"
                            aria-labelledby="headingOne"
                            data-parent="#accordion"
                          >
                            <div className="accordion__details">
                              {/* {upis.map((eachUpi: UpiItem, index: number) => (
                                <UpiComponent
                                  key={index}
                                  eachUpi={eachUpi}
                                  register={register}
                                  setValue={setValue}
                                />
                              ))} */}
                              {/* <a
                                type="button"
                                className="addUpi text--semi"
                                data-toggle="modal"
                                data-target="#upiPop"
                              >
                                <span className="plus-icon"></span>Add New UPI
                              </a> */}
                              <div className="input-container">
                                <p className="text--semi text--black insideCard__title sell_p">
                                  Enter UPI ID
                                </p>
                                <div className="input-form">
                                  <input
                                    maxLength={50}
                                    type="text"
                                    placeholder="Example@BankName"
                                    {...register('upiId')}
                                  />
                                </div>
                                {errors.upiId &&
                                  getValues('creditTo') === '' && (
                                    <p className="text--semi errorMessage withInfo showError ">
                                      {errors.upiId?.message}
                                    </p>
                                  )}
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* <div className="insideCard accordion">
                          <div
                            className="header"
                            id="headingTwo"
                            data-toggle="collapse"
                            data-target="#collapseTwo"
                            aria-expanded="false"
                            aria-controls="collapseTwo"
                          >
                            <p className="text--semi text--black insideCard__title sell_p">
                              Debit/Credit Card
                            </p>
                          </div>
                          <div
                            id="collapseTwo"
                            className="collapse"
                            aria-labelledby="headingTwo"
                            data-parent="#accordion"
                          >
                            <div className="accordion__details">
                              {[]?.map(
                                (eachDebitCard: any) => (
                                  <div className="bankRadio--row">
                                    <label className="radio__circle withOutline upi">
                                      <input
                                        type="radio"
                                        {...register("creditTo")}
                                        value={eachDebitCard?.cardNumber}
                                      />
                                      <span className="radio__circle-checkmark"></span>
                                      <div className="radio__circle-label d-flex align-items-center">
                                        <Image 
style={{ color: undefined }}
                                          className="img-fluid"
                                          src={sbiLogo}
                                          alt="SBI logo"
                                        />
                                        <div className="right">
                                          <p className="text--black text--bold title sell_p">
                                            SBI Debit{" "}
                                            {eachDebitCard?.cardNumber}
                                          </p>
                                          <p className="text--gray text--regular desc sell_p">
                                            {eachDebitCard?.cardHolderName}
                                          </p>
                                        </div>
                                      </div>
                                    </label>
                                  </div>
                                )
                              )}

                              <a
                                href="#"
                                className="addUpi text--semi"
                                data-toggle="modal"
                                data-target="#cardPop"
                              >
                                <span className="plus-icon"></span>Add New Card
                              </a>
                            </div>
                          </div>
                        </div> */}
                        <div className="input-container">
                          {errors?.creditTo &&
                            getValues('creditTo') === '' &&
                            getValues('upiId') === '' && (
                              <p className="text--semi errorMessage withInfo showError ">
                                {errors?.creditTo?.message}
                              </p>
                            )}
                        </div>

                        <div className="action-container">
                          <button
                            type="submit"
                            className={`custom__button custom__button--dark text--bold w-100 position-relative`}
                            id="DG_proceed-to-sell"
                          >
                            Proceed to sell
                            {/* {amountToPay.toString()}{' '} */}
                          </button>
                        </div>
                        <div className="power">
                          <div className="item">
                            <p className="text--gray text--semi title sell_p">
                              Powered by
                            </p>
                            <Image
                              style={{ color: undefined }}
                              className="img-fluid"
                              src={npci}
                              alt="npci"
                            />
                          </div>
                          <div className="item shield">
                            <Image
                              style={{ color: undefined }}
                              className="img-fluid"
                              src={shield}
                              alt="shield"
                            />
                            <p className="text--gray text--semi title sell_p">
                              24K | 99.9% Pure Gold
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </main>

      {/* <!-- Sell confirmation popup --> */}

      <div
        className={`modal loanDetails__modal repayModal visitSchedule pledge firstModal otpModal kycConfirmModal sellConfirm static`}
        id="sellConfirmModal"
        role="dialog"
        tabIndex={-1}
        aria-labelledby="sellConfirmModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <form
            className="modal-content"
            autoComplete="off"
            onSubmit={confirmSubmit(submitConfirm)}
          >
            <div className="header border-0 p-0">
              <div className="modal__title align-items-center">
                <div className="left">
                  <p className="text--bold text--black label sell_p mb-0">
                    Sell eSwarna Digital Gold?
                  </p>
                </div>
                <button data-dismiss="modal" ref={closeModalRef} type="button">
                  <Image
                    style={{ color: undefined }}
                    className="img-fluid"
                    src={closeIcon}
                    alt="close icon"
                    onClick={() => {
                      closeModalRef.current?.click();
                    }}
                  />
                </button>
              </div>
              <p className="text--regular text--gray desc">
                Are you sure you want to sell? If you sell today you will
                receive only{' '}
                <span className="text--bold text--black">
                  ₹ {amountToPay.toFixed(2)}
                </span>
              </p>
            </div>
            <div className="action-container mt-0">
              <button
                type="button"
                className="custom__button custom__button--dark border-btn backBtn text--bold w-100"
                data-dismiss="modal"
                onClick={() => {
                  closeModalRef?.current?.click();
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="custom__button custom__button--dark text--semi w-100"
                id="DG_pop-up_proceed-sell"
              >
                Proceed to Sell
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* <!-- upi popup --> */}
      {/* <AddUpiModal userId={userId} /> */}

      {/* <!-- bank popup --> */}
      <AddBankModal
        closeBankModal={() => closeBankModal()}
        bankModalRef={bankModalRef}
        userId={userId}
      />
    </>
  );
};
